/**
 * CashRegisterSessionController — page `/caisse-session.html?id=<uuid>`
 *
 * EPIC-043 / 43.4 — Fiche d'une session de caisse (§7.J) :
 *   - charge la session via GET /v1/cash-register/sessions/{id} ;
 *   - affiche la variante `openSection` ou `closedSection` selon `status` ;
 *   - render des mouvements (en cours) et transactions individuelles + agrégat
 *     (clôturée) via les templates `#movement-row-template` et
 *     `#transaction-row-template` ;
 *   - liens dynamiques vers `/caisse-{mouvement,transaction-en-attente}.html?id=…`.
 *
 * EPIC-099 / 99.19 — Refonte UX (§7.J.E + §7.J.F) :
 *   - `opening_balance` figé après ouverture : retrait de tout chemin UI
 *     d'édition (invariant §7.J.B) ;
 *   - clôture en bloc inline `#close-section` en bas de la fiche :
 *     calcul live des recettes anonymes, alerte écart négatif, confirmation
 *     `confirm.js` avant POST, re-render in-place en `closedSection` à la
 *     même URL après succès (pas de redirection) ;
 *   - bouton header *« Fermer la session »* = anchor scroll vers
 *     `#close-section` + focus sur le champ de comptage final.
 *
 * Convention `frontend/CLAUDE.md` : aucune construction HTML inline en JS —
 * uniquement clone de `<template>` + remplissage `data-slot`.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";
import confirm from "/js/services/confirm.js";

const STATUS_BADGE_LABEL = {
  open: "En cours",
  closed: "Clôturée",
};

const DIRECTION_LABEL = {
  in: "Entrée",
  out: "Sortie",
};

export default class CashRegisterSessionController extends BaseController {
  static targets = [
    "crumbLabel",
    "errorBanner",
    "openSection",
    "closedSection",
    "sessionTitle",
    "sessionSubtitle",
    "openingBalanceDisplay",
    "netMovementsDisplay",
    "individualSalesDisplay",
    "estimatedTotalDisplay",
    "closingCountDisplay",
    "anonymousReceiptsDisplay",
    "ownersLine",
    "movementsTable",
    "transactionsTable",
    "totalsRecap",
    "closeBtn",
    "movementBtn",
    "transactionBtn",
    "closingBalanceInput",
    "calculatedReceiptsDisplay",
    "negativeGapAlert",
    "negativeGapAmount",
    "attestSalesCheck",
    "attestMovementsCheck",
    "closeSubmitBtn",
  ];

  connect() {
    this._fetchSeq = 0;
    this._submitting = false;
    this._openingBalance = 0;
    this._netMovements = 0;
    this._individualSum = 0;
    this._sessionId = this._extractSessionId();
    if (!this._sessionId) {
      this._showErrorBanner("Session introuvable", "Identifiant de session manquant dans l'URL.");
      return;
    }
    this._fetch();
  }

  _extractSessionId() {
    const params = new URLSearchParams(window.location.search);
    return params.get("id") || null;
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async _fetch() {
    const seq = ++this._fetchSeq;
    try {
      const response = await api.cashRegister.getSession(this._sessionId);
      if (seq !== this._fetchSeq) return;
      // Le backend enveloppe la ressource dans `{data: {...}}` (success(['data' => …]))
      // alors que les anciens endpoints renvoyaient l'objet nu — on déballe les deux.
      const session = response?.data ?? response;
      this._renderSession(session);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger la session de caisse.";
      this._showErrorBanner("Erreur", msg);
    }
  }

  _renderSession(session) {
    if (!session || !session.status) {
      this._showErrorBanner("Erreur", "Réponse session incomplète.");
      return;
    }

    this._renderCommonHeader(session);
    this._renderActionButtons(session);

    const isOpen = session.status === "open";
    if (this.hasOpenSectionTarget) this.openSectionTarget.hidden = !isOpen;
    if (this.hasClosedSectionTarget) this.closedSectionTarget.hidden = isOpen;

    if (isOpen) {
      this._renderOpenTotals(session);
      this._renderMovementsTable(session);
      this._recalcAnonymousReceiptsFromState();
    } else {
      this._renderClosedTotals(session);
      this._renderOwnersLine(session);
      this._renderClosedTransactionsTable(session);
    }
  }

  _renderCommonHeader(session) {
    const dateLabel = this._formatDate(session.sessionDate);
    const titleLabel = "Session de caisse" + (session.label ? " — " + session.label : "");
    const statusLabel = STATUS_BADGE_LABEL[session.status] ?? session.status;

    if (this.hasCrumbLabelTarget) {
      this.crumbLabelTarget.textContent = `Session du ${dateLabel}`;
    }
    for (const titleEl of this.sessionTitleTargets) titleEl.textContent = titleLabel;
    for (const subtitleEl of this.sessionSubtitleTargets) {
      // Mise à jour DOM natif : TextNode date + <span class="badge"> statut
      // sans innerHTML — pattern conforme CLAUDE.md
      const textNode = document.createTextNode(`${dateLabel} · `);
      const badge = document.createElement("span");
      badge.className = "badge";
      badge.textContent = statusLabel;
      subtitleEl.replaceChildren(textNode, badge);
    }
  }

  _renderActionButtons(session) {
    const idEnc = encodeURIComponent(session.id);
    if (this.hasMovementBtnTarget) this.movementBtnTarget.setAttribute("href", `/caisse-mouvement.html?id=${idEnc}`);
    if (this.hasTransactionBtnTarget) this.transactionBtnTarget.setAttribute("href", `/caisse-transaction-en-attente.html?id=${idEnc}`);
    // closeBtn reste sur `#close-section` (anchor scroll, géré par scrollToCloseSection)
  }

  _renderOpenTotals(session) {
    const opening = Number(session.openingBalance) || 0;
    const movements = Array.isArray(session.manualMovements) ? session.manualMovements : [];
    const netMovements = movements.reduce((sum, m) => {
      const amount = Number(m.amount) || 0;
      return sum + (m.direction === "out" ? -amount : amount);
    }, 0);
    const individualSales = (Array.isArray(session.individualTransactions) ? session.individualTransactions : [])
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

    // Mémorise les valeurs pour le calcul live du bloc de clôture inline.
    this._openingBalance = opening;
    this._netMovements = netMovements;
    this._individualSum = individualSales;

    this._setTargetsText("openingBalanceDisplay", this._formatMoney(opening) + " €");
    this._setTargetsText("netMovementsDisplay", this._formatSigned(netMovements) + " €");
    this._setTargetsText("individualSalesDisplay", this._formatMoney(individualSales) + " €");
    this._setTargetsText("estimatedTotalDisplay", this._formatMoney(opening + netMovements + individualSales) + " €");
  }

  _renderClosedTotals(session) {
    const opening = Number(session.openingBalance) || 0;
    const closing = Number(session.closingBalance) || 0;
    const movements = Array.isArray(session.manualMovements) ? session.manualMovements : [];
    const netMovements = movements.reduce((sum, m) => {
      const amount = Number(m.amount) || 0;
      return sum + (m.direction === "out" ? -amount : amount);
    }, 0);
    // `individualTransactions` contient à la fois les vraies ventes
    // nominatives ET l'agrégat des recettes anonymes créé à la clôture
    // (cf. CashRegisterService::close()). On exclut l'agrégat pour ne pas
    // le compter en double — il s'affiche déjà sur la ligne « Recettes
    // anonymes ».
    const individualSales = (Array.isArray(session.individualTransactions) ? session.individualTransactions : [])
      .filter((t) => !this._isAnonymousReceiptsAggregate(t))
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    const anonymous = session.calculatedReceipts != null
      ? Number(session.calculatedReceipts) || 0
      : Math.max(0, closing - opening - netMovements - individualSales);

    this._setTargetsText("openingBalanceDisplay", this._formatMoney(opening) + " €");
    this._setTargetsText("netMovementsDisplay", this._formatSigned(netMovements) + " €");
    this._setTargetsText("individualSalesDisplay", this._formatMoney(individualSales) + " €");
    this._setTargetsText("closingCountDisplay", this._formatMoney(closing) + " €");
    this._setTargetsText("anonymousReceiptsDisplay", this._formatMoney(anonymous) + " €");
  }

  _renderOwnersLine(session) {
    if (!this.hasOwnersLineTarget) return;
    const openLabel = session.openedAt
      ? `Ouverture : ${this._formatDateTime(session.openedAt)}` + (session.openedByUserName ? ` par ${session.openedByUserName}` : "")
      : "Ouverture : —";
    const closeLabel = session.closedAt
      ? ` · Clôture : ${this._formatDateTime(session.closedAt)}` + (session.closedByUserName ? ` par ${session.closedByUserName}` : "")
      : "";
    this.ownersLineTarget.textContent = openLabel + closeLabel;
  }

  _renderMovementsTable(session) {
    if (!this.hasMovementsTableTarget) return;
    this._clearChildren(this.movementsTableTarget);

    const movements = Array.isArray(session.manualMovements) ? session.manualMovements : [];
    for (const movement of movements) {
      const fragment = this.cloneTemplate("movement-row-template");
      if (!fragment) continue;
      const amount = Number(movement.amount) || 0;
      const signed = movement.direction === "out" ? -Math.abs(amount) : Math.abs(amount);
      this.fillSlots(fragment, {
        "movement-direction": DIRECTION_LABEL[movement.direction] ?? movement.direction ?? "—",
        "movement-reason": movement.reason || "—",
        "movement-amount": this._formatSigned(signed) + " €",
        "movement-recorded-at": this._formatDateTime(movement.recordedAt),
      });
      this.movementsTableTarget.appendChild(fragment);
    }
  }

  _renderClosedTransactionsTable(session) {
    if (!this.hasTransactionsTableTarget) return;
    this._clearChildren(this.transactionsTableTarget);

    const movements = Array.isArray(session.manualMovements) ? session.manualMovements : [];
    const transactions = Array.isArray(session.individualTransactions) ? session.individualTransactions : [];

    // Mouvements manuels (entrées/sorties espèces hors vente).
    for (const movement of movements) {
      const fragment = this.cloneTemplate("transaction-row-template");
      if (!fragment) continue;
      const amount = Number(movement.amount) || 0;
      const signed = movement.direction === "out" ? -Math.abs(amount) : Math.abs(amount);
      this.fillSlots(fragment, {
        "tx-type": DIRECTION_LABEL[movement.direction] ?? "Mouvement",
        "tx-notes": movement.reason || "—",
        "tx-amount": this._formatSigned(signed) + " €",
        "tx-date": this._formatDateTime(movement.recordedAt),
      });
      const link = fragment.querySelector('[data-slot="tx-invoice-link"]');
      if (link) link.hidden = true;
      this.transactionsTableTarget.appendChild(fragment);
    }

    for (const tx of transactions) {
      const fragment = this.cloneTemplate("transaction-row-template");
      if (!fragment) continue;
      const amount = Number(tx.amount) || 0;
      const isAggregate = this._isAnonymousReceiptsAggregate(tx);
      this.fillSlots(fragment, {
        "tx-type": isAggregate ? "Recettes agrégées" : "Vente nominative",
        "tx-notes": tx.notes || "—",
        "tx-amount": this._formatMoney(amount) + " €",
        "tx-date": this._formatDateTime(tx.recordedAt ?? tx.createdAt ?? tx.transactionDate),
      });
      const link = fragment.querySelector('[data-slot="tx-invoice-link"]');
      if (link) {
        if (tx.invoiceId) {
          link.setAttribute("href", `/facture-detail.html?id=${encodeURIComponent(tx.invoiceId)}`);
          link.textContent = tx.invoiceNumber ? `Facture ${tx.invoiceNumber}` : "Voir la facture";
          link.hidden = false;
        } else {
          link.hidden = true;
        }
      }
      this.transactionsTableTarget.appendChild(fragment);
    }
  }

  // ---------------------------------------------------------------------------
  // Bloc de clôture inline (§7.J.F)
  // ---------------------------------------------------------------------------

  /**
   * Bouton header *« Fermer la session »* : scroll vers le bloc `#close-section`
   * + focus sur le champ de comptage final. L'`href="#close-section"` reste
   * fonctionnel sans JS (fallback no-script) ; on intercepte juste pour gérer
   * le focus explicite après le scroll.
   */
  scrollToCloseSection(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    const target = document.getElementById("close-section");
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    if (this.hasClosingBalanceInputTarget) {
      // Délai pour laisser le scroll s'amorcer avant le focus visible.
      window.setTimeout(() => {
        this.closingBalanceInputTarget.focus({ preventScroll: true });
      }, 150);
    }
  }

  /**
   * Calcul temps réel des recettes anonymes (BCMath-like, centimes entiers)
   * — déclenché à chaque frappe sur le champ comptage final.
   */
  recalcAnonymousReceipts() {
    this._recalcAnonymousReceiptsFromState();
  }

  _recalcAnonymousReceiptsFromState() {
    if (!this.hasClosingBalanceInputTarget) return;
    const raw = this.closingBalanceInputTarget.value;
    if (raw === "" || raw == null) {
      if (this.hasCalculatedReceiptsDisplayTarget) {
        this.calculatedReceiptsDisplayTarget.value = "— €";
      }
      if (this.hasNegativeGapAlertTarget) this.negativeGapAlertTarget.hidden = true;
      return;
    }
    const closing = Number(raw);
    if (!Number.isFinite(closing)) {
      if (this.hasCalculatedReceiptsDisplayTarget) {
        this.calculatedReceiptsDisplayTarget.value = "— €";
      }
      if (this.hasNegativeGapAlertTarget) this.negativeGapAlertTarget.hidden = true;
      return;
    }
    const cb = Math.round(closing * 100);
    const ob = Math.round(this._openingBalance * 100);
    const nm = Math.round(this._netMovements * 100);
    const is = Math.round(this._individualSum * 100);
    const cents = cb - ob - nm - is;
    const receipts = cents / 100;
    if (this.hasCalculatedReceiptsDisplayTarget) {
      this.calculatedReceiptsDisplayTarget.value = this._formatSigned(receipts) + " €";
    }
    if (this.hasNegativeGapAlertTarget) {
      if (cents < 0) {
        this.negativeGapAlertTarget.hidden = false;
        if (this.hasNegativeGapAmountTarget) {
          this.negativeGapAmountTarget.textContent = this._formatSigned(receipts) + " €";
        }
      } else {
        this.negativeGapAlertTarget.hidden = true;
      }
    }
  }

  /**
   * Soumission de la clôture : confirmation `confirm.js` → POST `/close` avec
   * `Idempotency-Key` → refetch + re-render in-place en `closedSection` à la
   * même URL (pas de redirection) + toast de confirmation §7.J.F.
   */
  async closeSession(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    if (this._submitting) return;
    if (!this.hasClosingBalanceInputTarget) return;
    if (!this._closeGateSatisfied()) return;

    const raw = this.closingBalanceInputTarget.value;
    const closing = raw === "" ? NaN : Number(raw);
    if (!Number.isFinite(closing) || closing < 0) {
      this._showErrorBanner("Erreur de saisie", "Le montant compté doit être un nombre supérieur ou égal à 0.");
      this.closingBalanceInputTarget.focus();
      return;
    }

    const ok = await confirm(
      "Une fois clôturée, cette session ne pourra plus être modifiée. Confirmer la clôture ?",
      { title: "Clôturer la session", confirmLabel: "Clôturer", cancelLabel: "Annuler" },
    );
    if (!ok) return;

    const payload = { closingBalance: closing.toFixed(2) };
    const idempotencyKey = this._generateIdempotencyKey();

    this._setSubmitting(true);
    this._hideErrorBanner();
    try {
      await api.cashRegister.closeSession(this._sessionId, payload, idempotencyKey);
      // Refetch puis re-render in-place (la session revient avec
      // status='closed' → la variante `closedSection` s'affiche).
      await this._fetch();
      // Calcul du montant agrégé pour le toast (verbatim spec §7.J.F).
      const cb = Math.round(closing * 100);
      const ob = Math.round(this._openingBalance * 100);
      const nm = Math.round(this._netMovements * 100);
      const is = Math.round(this._individualSum * 100);
      const cents = Math.max(0, cb - ob - nm - is);
      const receipts = cents / 100;
      this.showSuccess(`Session clôturée — ${this._formatMoney(receipts)} € enregistrés au livre des recettes`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      const status = error instanceof APIError ? error.status : 0;
      if (status === 409) {
        this._showErrorBanner("Session déjà clôturée", "Cette session est déjà clôturée.");
      } else if (status === 423) {
        this._showErrorBanner(
          "Exercice clôturé",
          "Vous ne pouvez pas clôturer cette session : l'exercice comptable est verrouillé.",
        );
      } else if (status === 422) {
        const msg = (error instanceof APIError && error.message) || "Données invalides.";
        this._showErrorBanner("Données invalides", msg);
      } else {
        const msg = (error instanceof APIError && error.message) || "Impossible de clôturer la session.";
        this._showErrorBanner("Erreur", msg);
      }
    } finally {
      this._setSubmitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _setSubmitting(submitting) {
    this._submitting = submitting;
    if (this.hasCloseSubmitBtnTarget) {
      this.closeSubmitBtnTarget.disabled = submitting || !this._closeGateSatisfied();
      this.closeSubmitBtnTarget.setAttribute("aria-busy", submitting ? "true" : "false");
    }
  }

  /**
   * Garde-fou client (§7.J) : la clôture n'est déverrouillée qu'une fois les
   * deux attestations cochées (plus de vente ≥ 76 € à saisir, tous les
   * mouvements enregistrés). Pas de persistance serveur — simple aide à la
   * saisie consciente avant une action irréversible.
   */
  _closeGateSatisfied() {
    const sales = this.hasAttestSalesCheckTarget ? this.attestSalesCheckTarget.checked : true;
    const movements = this.hasAttestMovementsCheckTarget ? this.attestMovementsCheckTarget.checked : true;
    return sales && movements;
  }

  updateCloseGate() {
    if (this._submitting || !this.hasCloseSubmitBtnTarget) return;
    this.closeSubmitBtnTarget.disabled = !this._closeGateSatisfied();
  }

  _setTargetsText(targetName, text) {
    const pluralProp = targetName + "Targets";
    const singularPresence = "has" + targetName.charAt(0).toUpperCase() + targetName.slice(1) + "Target";
    if (!this[singularPresence]) return;
    for (const el of this[pluralProp]) el.textContent = text;
  }

  _showErrorBanner(title, message) {
    if (!this.hasErrorBannerTarget) {
      this.showError(`${title} — ${message}`);
      return;
    }
    const titleEl = this.errorBannerTarget.querySelector('[data-slot="error-title"]');
    const msgEl = this.errorBannerTarget.querySelector('[data-slot="error-message"]');
    if (titleEl) titleEl.textContent = title;
    if (msgEl) msgEl.textContent = " " + message;
    this.errorBannerTarget.hidden = false;
  }

  _hideErrorBanner() {
    if (this.hasErrorBannerTarget) this.errorBannerTarget.hidden = true;
  }

  _generateIdempotencyKey() {
    return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : this._fallbackUuidV4();
  }

  _fallbackUuidV4() {
    const bytes = new Uint8Array(16);
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  /**
   * Détecte l'agrégat des recettes anonymes créé à la clôture par
   * CashRegisterService::close() : `clientName=null` + notes au format
   * « Recettes agrégées session caisse DD/MM/YYYY ». Permet d'éviter de
   * compter cette ligne dans la somme « Ventes nominatives » du récap et
   * de l'étiqueter correctement dans le tableau.
   */
  _isAnonymousReceiptsAggregate(tx) {
    return tx
      && !tx.invoiceId
      && !tx.clientName
      && typeof tx.notes === "string"
      && /^Recettes agrégées/i.test(tx.notes);
  }

  _clearChildren(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  _formatMoney(raw) {
    const num = Number(raw);
    if (!Number.isFinite(num)) return "0,00";
    return num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  _formatSigned(raw) {
    const num = Number(raw);
    if (!Number.isFinite(num)) return "0,00";
    if (num === 0) return "0,00";
    const formatted = Math.abs(num).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (num < 0 ? "−" : "+") + formatted;
  }

  _formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  }

  _formatDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}
